import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useApolloClient } from '@apollo/client'
import * as Sentry from '@sentry/nextjs'
import {
  applyActionCode as firebaseApplyActionCode,
  AuthError,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  connectAuthEmulator,
  getAuth,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  updatePassword,
} from 'firebase/auth'
import { once } from 'lodash'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { Route } from 'constants/routes'
import {
  BrandInfoFragment,
  QueryUserMeArgs,
  useHasActiveSubscriptionLazyQuery,
  useIsUserEmailConfirmedLazyQuery,
  UserMeDocument,
  UserMeQuery,
  UserType,
  VenueInfoFragment,
} from 'generated/generated-graphql'
import {
  AnalyticsLoginMethod,
  setAnalyticsUser,
  trackAnalyticsEvent,
} from 'utils/analytics'
import { FirebaseApp } from 'utils/firebase'
import {
  removeSavedSelectedCompanyId,
  setSavedSelectedCompanyId,
} from 'utils/handleSavedSelectedCompany'
import { UserRoles } from 'utils/roles'
import { useErrorToast } from 'utils/toast'

export type OmittedUserData = UserMeQuery['userMe']

interface EmailAndPasswordInput {
  email: string
  password: string
}

interface SignInWithEmailAndPasswordInput extends EmailAndPasswordInput {
  // Returns true when the invalid credentials are handled by the caller, the default error toast is not shown then
  handleInvalidCredentials?: () => Promise<boolean>
}

interface ConfirmPasswordResetInput {
  resetCode: string
  password: string
}

interface ConfirmEmailInput {
  oobCode: string
}

interface UserContextType {
  isLoading: boolean
  isFirebaseInitializing: boolean
  userData: OmittedUserData | null
  signOut: (redirectTo?: string) => Promise<void>
  signInWithEmailAndPassword: ({
    email,
    password,
    handleInvalidCredentials,
  }: SignInWithEmailAndPasswordInput) => Promise<void>
  signInWithFirebaseToken: (token: string) => Promise<void>
  refetchAndSetUserData: (input?: {
    activeVenueId?: number
    activeBrandId?: number
  }) => Promise<void>
  setActiveCompany: (
    selectedComapny: VenueInfoFragment | BrandInfoFragment | null
  ) => void
  sendResetPasswordEmail: ({ email }: { email: string }) => Promise<boolean>
  confirmPasswordReset: ({
    resetCode,
    password,
  }: ConfirmPasswordResetInput) => Promise<boolean>
  applyActionCode: ({ oobCode }: ConfirmEmailInput) => Promise<boolean>
  changePassword: (newPassword: string) => Promise<void> | null
  isVenue: boolean
  isBrand: boolean
  canCreateTender: boolean
  requiresParamountLiquorIntegration: boolean
  userRoleInActiveCompany: UserRoles | null
}

interface Props {
  children: ReactNode
}

const GENERAL_PERSISTS_ERROR = 'TRANSLATION_135'

/**
 * Firebase email action codes (`oobCode`) are single use and short lived, so
 * these failures are an expected part of the flow rather than a defect: the
 * link was already opened (email scanners and link previews frequently
 * consume it first), it expired, or a newer email superseded it. Callers
 * recover by requesting a fresh email, so they are not reported to Sentry.
 */
const EXPECTED_ACTION_CODE_ERROR_CODES = [
  'auth/invalid-action-code',
  'auth/expired-action-code',
]

export const FirebaseAuth = getAuth(FirebaseApp)

if (process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST) {
  once(() => {
    connectAuthEmulator(
      FirebaseAuth,
      process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST!
    )
  })()
}

const UserContext = createContext({} as UserContextType)

export const useAuth = (): UserContextType => useContext(UserContext)

const UserProvider = ({ children }: Props) => {
  const { t } = useTranslation('common')
  const [userData, setUserData] = useState<OmittedUserData | null>(null)
  const [userRole, setUserRole] = useState<UserRoles | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isFirebaseInitializing, setIsFirebaseInitializing] = useState(true)
  const errorToast = useErrorToast()
  const router = useRouter()
  const apolloClient = useApolloClient()
  const [refetchActiveSubscription] = useHasActiveSubscriptionLazyQuery()

  const isVenue = userData?.userType === UserType.Venue
  const isBrand = userData?.userType === UserType.Brand
  const canCreateTender = Boolean(
    isVenue && userData.activeVenue?.canCreateTender
  )
  const requiresParamountLiquorIntegration = Boolean(
    isVenue && userData.activeVenue?.requiresParamountLiquorIntegration
  )

  const [checkEmailConfirmation] = useIsUserEmailConfirmedLazyQuery({
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (isFirebaseInitializing) {
      return
    }
    setAnalyticsUser(
      userData && {
        id: userData.id,
        userType: userData.userType,
        region: userData.region,
        activeCompany: userData.activeBrand ?? userData.activeVenue,
        subscriptionTier: userData.activeBrand?.subscriptionTier,
        venueCategoryName: userData.activeVenue?.venueCategoryName,
      }
    )
  }, [isFirebaseInitializing, userData])

  const signInWithEmailAndPassword = useCallback(
    async ({
      email,
      password,
      handleInvalidCredentials,
    }: SignInWithEmailAndPasswordInput) => {
      try {
        setIsLoading(true)
        removeSavedSelectedCompanyId()
        await firebaseSignInWithEmailAndPassword(FirebaseAuth, email, password)
        trackAnalyticsEvent('login', {
          method: AnalyticsLoginMethod.EmailAndPassword,
        })
      } catch (error) {
        const err = error as AuthError
        switch (err.code) {
          // Firebase returns the generic `auth/invalid-credential` instead of
          // `auth/wrong-password`/`auth/user-not-found` when email enumeration
          // protection is enabled.
          case 'auth/invalid-credential':
          case 'auth/invalid-email':
          case 'auth/wrong-password':
          case 'auth/user-not-found':
            if (await handleInvalidCredentials?.()) {
              break
            }
            errorToast({
              description: t('TRANSLATION_136'),
            })
            break
          case 'auth/network-request-failed':
            errorToast({
              description: t('TRANSLATION_140'),
            })
            break
          // Firebase temporarily blocks the account after repeated failed
          // sign-in attempts. This is expected, so it is not sent to Sentry.
          case 'auth/too-many-requests':
            errorToast({
              description: t('signInTooManyRequests'),
            })
            break
          default:
            Sentry.captureException(err)
            errorToast({
              description: t(GENERAL_PERSISTS_ERROR),
            })
        }
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [errorToast, t]
  )

  const signInWithFirebaseToken = useCallback(
    async (token: string) => {
      try {
        setIsLoading(true)
        removeSavedSelectedCompanyId()
        await firebaseSignInWithCustomToken(FirebaseAuth, token)
        trackAnalyticsEvent('login', {
          method: AnalyticsLoginMethod.FirebaseToken,
        })
      } catch (error) {
        const err = error as AuthError
        switch (err.code) {
          case 'auth/invalid-email':
          case 'auth/wrong-password':
          case 'auth/user-not-found':
            errorToast({
              description: t('TRANSLATION_136'),
            })
            break
          case 'auth/network-request-failed':
            errorToast({
              description: t('TRANSLATION_140'),
            })
            break
          default:
            Sentry.captureException(err)
            errorToast({
              description: t(GENERAL_PERSISTS_ERROR),
            })
        }
      } finally {
        setIsLoading(false)
      }
    },
    [errorToast, t]
  )

  const signOut = useCallback(
    async (redirectTo?: string) => {
      try {
        setIsLoading(true)

        setUserData(null)
        removeSavedSelectedCompanyId()

        await FirebaseAuth.signOut()

        // Do not reset the cache here: `cache.reset()` re-fires every active
        // query on the page, now without a token, and each `Unauthorized`
        // surfaces as a toast before the redirect lands. `stop()` tears the
        // active queries down instead, and the full-page navigation below
        // discards the in-memory cache anyway.
        apolloClient.stop()

        window.location.replace(redirectTo ?? Route.SignIn())
      } catch (error) {
        Sentry.captureException(error)
      } finally {
        setIsLoading(false)
      }
    },
    [apolloClient]
  )

  const changePassword = useCallback(
    (newPassword: string) =>
      FirebaseAuth.currentUser &&
      updatePassword(FirebaseAuth.currentUser, newPassword),
    []
  )

  const setUserRoleInActiveCompany = useCallback(
    async (
      userId?: number,
      activeCompany?:
        | OmittedUserData['activeBrand']
        | OmittedUserData['activeVenue']
        | null
    ) => {
      const userDataInActiveCompany = activeCompany?.users.find(
        (user) => user.userId === userId
      )
      if (!userDataInActiveCompany) {
        setUserRole(null)
        return
      }

      setUserRole(userDataInActiveCompany.role as UserRoles)
    },
    []
  )

  const refetchAndSetUserData = useCallback(
    async ({
      activeVenueId,
      activeBrandId,
    }: { activeVenueId?: number; activeBrandId?: number } = {}) => {
      try {
        if (!FirebaseAuth.currentUser) {
          return
        }

        const response = await apolloClient.query<UserMeQuery, QueryUserMeArgs>(
          {
            query: UserMeDocument,
            ...((activeVenueId || activeBrandId) && {
              variables: { input: { activeVenueId, activeBrandId } },
            }),
            fetchPolicy: 'network-only',
          }
        )
        if (response.errors && response.errors.length > 0) {
          response.errors.forEach((error) => {
            Sentry.withScope((scope) => {
              scope.setContext('Additional Data', { scope })
              Sentry.captureException(new Error(error.message))
            })
          })

          throw new Error(
            response.errors[0]?.message ?? 'Error fetching user data'
          )
        }
        if (!response.data?.userMe) {
          throw new Error(t(GENERAL_PERSISTS_ERROR))
        }

        const user = response.data.userMe

        Sentry.setUser({ email: user.email })

        setUserData((prevData) => {
          const activeVenue = user.activeVenue ?? prevData?.activeVenue ?? null
          const activeBrand = user.activeBrand ?? prevData?.activeBrand ?? null

          setUserRoleInActiveCompany(user.id, activeVenue ?? activeBrand)

          if (activeBrand) {
            refetchActiveSubscription({
              fetchPolicy: 'network-only',
              variables: {
                brandId: activeBrand.id,
              },
            })
          }

          return {
            ...user,
            activeBrand,
            activeVenue,
          }
        })
      } catch (error) {
        errorToast({
          title: t('signInUnauthorized'),
          description: t('signInError'),
        })
        Sentry.captureException(error)
        // Hot Reload may cause relog if any request fails
        // In production we should sign out user when he experience signing in issues
        if (process.env.NODE_ENV !== 'development') {
          await signOut()
        }
      }
    },
    [
      apolloClient,
      errorToast,
      refetchActiveSubscription,
      setUserRoleInActiveCompany,
      signOut,
      t,
    ]
  )

  const setActiveCompany = useCallback(
    (
      selectedCompany:
        | OmittedUserData['activeBrand']
        | OmittedUserData['activeVenue']
        | null
    ) => {
      setUserData((prevData) =>
        prevData
          ? {
              ...prevData,
              ...(isBrand &&
                selectedCompany?.__typename === 'Brand' && {
                  activeBrand: selectedCompany,
                }),
              ...(isVenue &&
                selectedCompany?.__typename === 'Venue' && {
                  activeVenue: selectedCompany,
                }),
            }
          : null
      )
      if (selectedCompany && userData?.id) {
        setSavedSelectedCompanyId({
          companyId: selectedCompany?.id,
          userId: userData?.id,
        })
      }

      setUserRoleInActiveCompany(userData?.id, selectedCompany)
      if (selectedCompany?.__typename === 'Brand') {
        refetchActiveSubscription({
          fetchPolicy: 'network-only',
          variables: {
            brandId: selectedCompany.id,
          },
        })
      }
    },
    [
      isBrand,
      isVenue,
      refetchActiveSubscription,
      setUserRoleInActiveCompany,
      userData?.id,
    ]
  )

  const sendResetPasswordEmail = useCallback(
    async ({ email }: { email: string }) => {
      try {
        setIsLoading(true)
        await firebaseSendPasswordResetEmail(FirebaseAuth, email)
        setIsLoading(false)
        return true
      } catch (error) {
        const err = error as AuthError
        switch (err.code) {
          case 'auth/invalid-email':
          case 'auth/user-not-found':
            errorToast({
              title: t('TRANSLATION_138'),
              description: t('TRANSLATION_139'),
            })
            break
          case 'auth/network-request-failed':
            errorToast({
              title: t('TRANSLATION_138'),
              description: t('TRANSLATION_140'),
            })
            break
          default:
            Sentry.captureException(err)
            errorToast({
              description: GENERAL_PERSISTS_ERROR,
            })
        }
        setIsLoading(false)
        return false
      }
    },
    [errorToast, t]
  )

  // TODO change redirect URL in Firebase console from localhost:3000 to real one
  const confirmPasswordReset = useCallback(
    async ({ resetCode, password }: ConfirmPasswordResetInput) => {
      try {
        setIsLoading(true)
        await firebaseConfirmPasswordReset(FirebaseAuth, resetCode, password)
        setIsLoading(false)
        return true
      } catch (error) {
        Sentry.captureException(error)
        setIsLoading(false)
        return false
      }
    },
    []
  )

  const applyActionCode = useCallback(
    async ({ oobCode }: ConfirmEmailInput) => {
      try {
        setIsLoading(true)
        await firebaseApplyActionCode(FirebaseAuth, oobCode)
        setIsLoading(false)
        return true
      } catch (error) {
        if (
          !EXPECTED_ACTION_CODE_ERROR_CODES.includes((error as AuthError).code)
        ) {
          Sentry.captureException(error)
        }
        setIsLoading(false)
        return false
      }
    },
    []
  )

  useEffect(() => {
    const subscribe = FirebaseAuth.onAuthStateChanged((firebaseUser) => {
      if (
        router.asPath === Route.FirebaseSignIn() ||
        isLoading ||
        userData?.activeBrand ||
        userData?.activeVenue
      ) {
        /**
         * We don't want to fetch user when he's on firebaseSignInPage, since he
         * will most likely be signing in as another user
         * HOWEVER: there may be a bug where some signed-in user navigates
         * to this page, and he will be redirected to dashboard, but his data
         * won't be fetched because this observer won't be re-mounted. Keep this
         * in mind when discovering future bugs.
         */
        return
      }
      if (firebaseUser) {
        void (async () => {
          await checkEmailConfirmation()
          await refetchAndSetUserData()
          setIsFirebaseInitializing(false)
        })()
      } else {
        setUserRole(null)
        setUserData(null)
        setIsFirebaseInitializing(false)
        setActiveCompany(null)
      }
    })
    return subscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  const contextValue = useMemo(
    () => ({
      userData,
      isLoading,
      isFirebaseInitializing,
      signInWithEmailAndPassword,
      signInWithFirebaseToken,
      signOut,
      refetchAndSetUserData,
      setActiveCompany,
      sendResetPasswordEmail,
      confirmPasswordReset,
      applyActionCode,
      isVenue,
      isBrand,
      canCreateTender,
      requiresParamountLiquorIntegration,
      changePassword,
      userRoleInActiveCompany: userRole,
    }),
    [
      userData,
      isLoading,
      isFirebaseInitializing,
      signInWithEmailAndPassword,
      signInWithFirebaseToken,
      signOut,
      refetchAndSetUserData,
      setActiveCompany,
      sendResetPasswordEmail,
      confirmPasswordReset,
      applyActionCode,
      isVenue,
      isBrand,
      canCreateTender,
      requiresParamountLiquorIntegration,
      changePassword,
      userRole,
    ]
  )

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  )
}
export default UserProvider
